import { FunctionComponent, useEffect, useMemo } from "react";
import styles from "./Styling.module.css";
import SectionTitle from "../../components/SectionTitle";
import ColumnButton from "../../components/ColumnButton";
import { CAMERA_XY, CAMERA_YZ, MARKER_STYLE_INITIAL } from "../../constants";
import Switch from "../../components/Switch";
import { useDispatch, useSelector } from "react-redux";
import { rootActions, TRightPanel } from "../../reducers/root.slice";
import { TRootState } from "../../store/create-store";
import debounce from "lodash.debounce";
import withErrorBoundary from "../../hocs/withErrorBoundary";
import Icon from "../../components/Icon";
import MathJax from "../../components/MathJax";
import classNames from "classnames";
import { selectRightPanelState } from "../../selectors";
import { CAMERA_XZ } from "../../constants";
import { isCameraPreset } from "../../utils/utils";

const Styling: FunctionComponent = () => {
  const {
    showAxes,
    markerOpacity,
    markerSize,
    markerLineColor,
    showCorrelations,
    showProjectionX,
    showProjectionY,
    showProjectionZ,
    camera,
  } = useSelector(selectPointStyle);
  const dispatch = useDispatch();

  const onStyleChange = (style: Partial<TRightPanel>) => {
    dispatch(rootActions.setRightPanel(style));
  };

  const onCameraChange = (camera: Partial<TRightPanel>) => {
    dispatch(rootActions.setRightPanel(camera));
  };

  const debouncedGenerateGraph = useMemo(() => {
    // TODO this should not be called on mount
    return debounce(() => {
      dispatch(rootActions.generateGraph());
    }, 100);
  }, []);

  useEffect(debouncedGenerateGraph, [
    markerLineColor,
    markerOpacity,
    markerSize,
    showProjectionX,
    showProjectionY,
    showProjectionZ,
  ]);

  return (
    <div className={styles.fields}>
      <SectionTitle>Space</SectionTitle>

      <ul>
        <li className={styles.listItem}>
          <label className={styles.field}>
            <Icon name="line_axis" /> Axes
          </label>

          <Switch
            value={showAxes}
            onChange={() => {
              dispatch(rootActions.setRightPanel({ showAxes: !showAxes }));
            }}
          />
        </li>

        <li className={styles.listItem}>
          <span className={styles.field}>
            <Icon name="cameraswitch" /> <span>Perspective</span>
          </span>

          <div className={styles.presets}>
            <ColumnButton
              className={styles.button}
              onClick={() => onCameraChange({ camera: CAMERA_XZ })}
              isActive={isCameraPreset(camera, CAMERA_XZ)}
            >
              XZ
            </ColumnButton>

            <ColumnButton
              className={styles.button}
              onClick={() => onCameraChange({ camera: CAMERA_YZ })}
              isActive={isCameraPreset(camera, CAMERA_YZ)}
            >
              YZ
            </ColumnButton>

            <ColumnButton
              className={styles.button}
              onClick={() => onCameraChange({ camera: CAMERA_XY })}
              isActive={isCameraPreset(camera, CAMERA_XY)}
            >
              XY
            </ColumnButton>
          </div>
        </li>

        <li className={styles.listItem}>
          <span className={styles.field}>
            <Icon name="videocam" /> <span>Projections</span>
          </span>

          <ul className={styles.inlineList}>
            <li className={styles.inlineListItem}>
              <label
                className={classNames(styles.field, styles.fieldWithHeight)}
              >
                X
              </label>

              <Switch
                value={showProjectionX}
                onChange={() => {
                  dispatch(
                    rootActions.setRightPanel({
                      showProjectionX: !showProjectionX,
                    })
                  );
                }}
              />
            </li>
            <li className={styles.inlineListItem}>
              <label
                className={classNames(styles.field, styles.fieldWithHeight)}
              >
                Y
              </label>

              <Switch
                value={showProjectionY}
                onChange={() => {
                  dispatch(
                    rootActions.setRightPanel({
                      showProjectionY: !showProjectionY,
                    })
                  );
                }}
              />
            </li>

            <li className={styles.inlineListItem}>
              <label
                className={classNames(styles.field, styles.fieldWithHeight)}
              >
                Z
              </label>

              <Switch
                value={showProjectionZ}
                onChange={() => {
                  dispatch(
                    rootActions.setRightPanel({
                      showProjectionZ: !showProjectionZ,
                    })
                  );
                }}
              />
            </li>
          </ul>
        </li>
      </ul>

      <SectionTitle>Information</SectionTitle>

      <ul>
        <li className={styles.listItem}>
          <label className={classNames(styles.field, styles.fieldWithHeight)}>
            <MathJax tex="dCor R^2 \& PCA" />
          </label>
          <Switch
            value={showCorrelations}
            onChange={() => {
              dispatch(
                rootActions.setRightPanel({
                  showCorrelations: !showCorrelations,
                })
              );
            }}
          />
        </li>
      </ul>

      <SectionTitle>Marker</SectionTitle>

      <ul>
        <li className={styles.listItem}>
          <label className={styles.field}>
            <Icon name="opacity" />
            Opacity
          </label>

          <span className={styles.inlineListItem}>
            <input
              className={styles.range}
              type="range"
              name="markerOpacity"
              min="0.1"
              max="1"
              step="0.1"
              value={String(markerOpacity)}
              onChange={(event) =>
                onStyleChange({
                  markerOpacity: Number(event.target.value),
                })
              }
            />
            {markerOpacity}
          </span>
        </li>

        <li className={styles.listItem}>
          <label className={styles.field}>
            <Icon name="format_size" /> Size
          </label>

          <span className={styles.inlineListItem}>
            <input
              className={styles.range}
              type="range"
              name="markerSize"
              min="1"
              max="10"
              step="1"
              value={String(markerSize)}
              onChange={(event) =>
                onStyleChange({
                  markerSize: Number(event.target.value),
                })
              }
            />
            {markerSize}
          </span>
        </li>
      </ul>

      <SectionTitle>Style Presets</SectionTitle>

      <div className={styles.presets}>
        <ColumnButton
          className={styles.button}
          onClick={() => {
            onStyleChange({
              markerLineColor: MARKER_STYLE_INITIAL.line.color,
              markerSize: MARKER_STYLE_INITIAL.size,
              markerOpacity: MARKER_STYLE_INITIAL.opacity,
            });
          }}
          isActive={
            markerLineColor === MARKER_STYLE_INITIAL.line.color &&
            markerSize === MARKER_STYLE_INITIAL.size &&
            markerOpacity === MARKER_STYLE_INITIAL.opacity
          }
        >
          Default
        </ColumnButton>

        <ColumnButton
          onClick={() => {
            onStyleChange({
              markerLineColor: "transparent",
              markerSize: 8,
              markerOpacity: 0.7,
            });
          }}
          isActive={
            markerLineColor === "transparent" &&
            markerSize === 8 &&
            markerOpacity === 0.7
          }
        >
          Translucent
        </ColumnButton>
      </div>

      {/*{categories.length > 0 && (*/}
      {/*  <>*/}
      {/*    <SectionTitle>Marker Shape</SectionTitle>*/}

      {/*    {categories.map((category) => (*/}
      {/*      <label className={styles.field}>*/}
      {/*        {category}*/}
      {/*        <Select*/}
      {/*          selected={undefined}*/}
      {/*          onChange={(symbol: string) =>*/}
      {/*            onMarkerSymbolChange(category, symbol)*/}
      {/*          }*/}
      {/*          placeholder="Select shape"*/}
      {/*          options={["circle", "square", "triangle"]}*/}
      {/*        />*/}
      {/*      </label>*/}
      {/*    ))}*/}
      {/*  </>*/}
      {/*)}*/}
    </div>
  );
};

const selectPointStyle = (state: TRootState) => {
  const {
    showAxes,
    showCorrelations,
    showProjectionX,
    showProjectionZ,
    showProjectionY,
    markerOpacity,
    markerSize,
    markerLineColor,
    camera,
  } = selectRightPanelState(state);

  return {
    showAxes,
    showCorrelations,
    showProjectionX,
    showProjectionY,
    showProjectionZ,
    markerOpacity,
    markerSize,
    markerLineColor,
    camera,
  };
};

export default withErrorBoundary(Styling);
